import { CommandSource, MutationType } from '@tencent/slide-sdk-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import type { ISelection } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import type { ITextSelectionUndoRedo, StashedSelection, History, Batch, TableCellCoordinate, TextSelectionRelatedModel } from '@src/slide/application/service/text-selection-undo-redo/interface';
import { MaxUndos, TextMutations } from '@src/slide/application/service/text-selection-undo-redo/constant';
import type { ITextEditor } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import { isShapeModel, isSlideModel, isTableModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';

/**
 * 在触发 sdk 数据 undo redo 操作时，记录并还原涉及到的文字选区和光标位置
 * 由于文本不是实时保存的，所以在 sdk 每一次 undo redo 都会涉及到多个文本的变化，而且选区和光标需要和当前的数据对应上
 * 所以 undo 需要还原为变更之前数据对应的选区，redo 需要还原为变更之后数据对应的选区
 *
 * 这里记录文字选区还原的策略：
 * undo：记录两次数据变更期间第一次修改文本数据前的选区，触发数据保存后需要重置
 * redo：记录触发数据保存前的选区，由于编辑过程中无法感知到文本的保存时机，所以需要在编辑过程中持续缓存当前选区，数据保存后重置
 */
export class TextSelectionUndoRedo implements ITextSelectionUndoRedo {
    // 暂存的选区信息
    private stashedSelection: StashedSelection | null = null;
    private editorInstanceService: IEditorInstanceService | null = null;
    // 文字选区 undo redo 栈
    private history: History = {
        undos: [],
        redos: [],
    };
    private undoRedoCallBacks: Set<() => void> = new Set();

    public constructor() {
        this.initUndoRedoListener();
    }

    public setEditorInstanceServiceDelegate(editorInstanceService: IEditorInstanceService) {
        this.editorInstanceService = editorInstanceService;
    }

    /**
     * 暂存两次数据保存之间第一次修改文本前的选区
     */
    public stashSelectionBefore(
        selection: ISelection,
        modelId: string | null,
        tableCellCoordinate?: TableCellCoordinate,
    ) {
        if (this.stashedSelection?.selectionBefore) {
            return;
        }
        this.stashedSelection = {
            selectionBefore: selection,
            modelId,
            tableCellCoordinate,
            // 以当前选区作为 selectionAfter 的初始值，如果数据修改后选区有变化，那么当前值自动会被重置，否则就表示选区没有变化
            selectionAfter: selection,
        };
    }

    /**
     * 暂存数据修改后的文字选区
     * 由于数据保存时文本框可能已经失焦，无法准确获取选区，所以在选区变化时暂存，在数据保存时使用上一次暂存的选区作为数据修改后的选区
     */
    public stashSelectionAfter(
        selection: ISelection,
        modelId: string | null,
        tableCellCoordinate?: TableCellCoordinate,
    ) {
        if (modelId !== this.stashedSelection?.modelId) {
            // modelId 不匹配，说明 stashSelectionBefore 还没触发，此时没必要暂存选区
            return;
        }
        if (
            tableCellCoordinate?.col !== this.stashedSelection.tableCellCoordinate?.col
            || tableCellCoordinate?.row !== this.stashedSelection.tableCellCoordinate?.row
        ) {
            // 表格单元格坐标不匹配，说明选区变化时不在同一个单元格，不需要暂存
            return;
        }

        if (!selection) {
            return;
        }
        this.stashedSelection.selectionAfter = selection;
    }

    public clearRedo() {
        this.history.redos = [];
    }

    /**
     * 触发选区的 undo redo
     * @param source
     * @param commandId
     * @param selectedModel 当前选中的 model，表示新的选区在这个 model 对应的元素上
     * @returns
     */
    public triggerUndoRedo(source: CommandSource, commandId: string, selectedModel: TextSelectionRelatedModel) {
        if (source === CommandSource.Undo) {
            this.handleUndo(commandId, selectedModel);
            this.executeCallbacks();
            return;
        }
        if (source === CommandSource.Redo) {
            this.executeCallbacks();
            this.handleRedo(commandId, selectedModel);
            return;
        }
    }

    /**
     * 添加 undo redo 选区变化之后的回调
     * @param callback
     * @returns 返回值为清除函数
     */
    public afterUndoRedo(callback: () => void): () => void {
        this.undoRedoCallBacks.add(callback);
        return () => {
            this.undoRedoCallBacks.delete(callback);
        };
    }

    private executeCallbacks() {
        this.undoRedoCallBacks.forEach(callback => callback());
    }

    /**
     * 监听 sdk 中文字保存逻辑，在数据保存时暂存选区
     */
    private initUndoRedoListener() {
        SlideDataServices.SDKDataServices.EventManager.promise().then(({ commandEvents }) => {
            commandEvents.onUndoRedoChange((res) => {
                const { mutationsType, source, commandId } = res;
                if (source === CommandSource.Editor) {
                    this.handleEdit(mutationsType, commandId);
                    return;
                }
            });
        });
    }

    /**
     * 触发编辑保存，需要在 undo 栈中添加编辑前后的选区数据
     */
    private handleEdit(mutationsType: MutationType[], commandId: string) {
        // 这次编辑提交和文本相关
        if (this.stashedSelection && mutationsType.some(type => TextMutations.includes(type))) {
            const batch = {
                commandId,
                ...this.stashedSelection,
            };
            this.writeHistory('undos', batch);
        }
        // 数据保存后，清除暂存选区
        this.stashedSelection = null;
        // 数据保存后，原有 redo 栈无效，直接清空
        this.clearRedo();
    }

    /**
     * sdk undo 触发，执行选区 undo
     */
    private handleUndo(commandId: string, selectedModel: TextSelectionRelatedModel) {
        // 触发 undo
        const { undos } = this.history;
        if (undos.length > 0) {
            const undo = undos[undos.length - 1];
            if (commandId === undo.commandId) {
                // 当前 undo id 和栈顶 id 一致，则执行选区的 undo，并执行 redo 入栈
                const batch = undos.pop() as Batch;
                this.updateTextSelection(
                    selectedModel,
                    batch.selectionBefore,
                    batch.modelId,
                    batch.tableCellCoordinate,
                );
                this.writeHistory('redos', batch);
            }
        }
    }

    /**
     * sdk redo 触发，执行选区 redo
     */
    private handleRedo(commandId: string, selectedModel: TextSelectionRelatedModel) {
        // 触发 redo
        const { redos } = this.history;
        if (redos.length > 0) {
            const lastRedo = redos[redos.length - 1];
            if (commandId === lastRedo.commandId) {
                // 当前 redo id 和栈顶 id 一致，则执行选区的 redo，并执行 undo 入栈
                const batch = redos.pop() as Batch;
                this.updateTextSelection(selectedModel, batch.selectionAfter, batch.modelId, batch.tableCellCoordinate);
                this.writeHistory('undos', batch);
            }
        }
    }

    private updateTextSelection(
        selectedModel: TextSelectionRelatedModel,
        selection: ISelection,
        modelId: string | null,
        tableCellCoordinate?: TableCellCoordinate,
    ) {
        if (modelId !== selectedModel.id) {
            return;
        }
        let textEditor: ITextEditor | null = null;
        if (isSlideModel(selectedModel)) {
            textEditor = selectedModel.noteData.noteTextBody.editorInstance;
        } else if (isShapeModel(selectedModel)) {
            textEditor = selectedModel.editorInstance;
        } else if (isTableModel(selectedModel) && tableCellCoordinate) {
            const tableCell = selectedModel.tableItems?.[tableCellCoordinate.row]?.[tableCellCoordinate.col];
            textEditor = tableCell?.editorInstance;
        }
        if (textEditor) {
            const currentEditor = this.editorInstanceService?.getEditorInstance();
            if (currentEditor && textEditor !== currentEditor) {
                // 如果当前在编辑态的 editor 不是 textEditor，直接让 textEditor 失焦
                currentEditor?.triggerBlur();
            }
            textEditor.updateSelection(selection, true);
        }
    }

    private writeHistory(stack: 'undos' | 'redos', batch: Batch) {
        this.history[stack].push(batch);
        // 限制 undo 栈长度，避免无限增长
        if (stack === 'undos' && this.history.undos.length > MaxUndos) {
            this.history.undos.shift();
        }
    }
}
